/**
 * Highstreet colour tokens for the Outlook task-pane add-in — the single
 * source of truth for this app's palette.
 *
 * Consumed by:
 *   - `tailwind.config.ts` (spread into `theme.extend.colors`, so every
 *     `bg-*` / `text-*` / `border-*` utility resolves from this file)
 *   - `src/theme.ts` and any MUI `sx` / `createTheme` object, via
 *     `getColorHexCodeByColorName` — MUI takes colour *values*, not classes,
 *     and this keeps those values from drifting away from the utilities.
 *
 * Mirrors the matching subset of `product/ui/tailwind.config.ts` so designs
 * port across ora and the add-in 1:1. When the main app adds a token we want
 * here, copy it across — there is no shared package today.
 *
 * ora has the same accessor at `app/common/utilities/pageUtils.ts`; it is not
 * reused here on purpose, because importing it would pull ora's Tailwind
 * config (and its route constants) into the add-in bundle.
 */

export const colors = {
  lotus: "#FFDD00",
  sunrise: "#FFC600",
  blossom: "#FFA400",
  lemon: "#FFF3A5",
  sunshine: "#FFEB68",
  dividergray: "#A1A1A1",
  base: {
    white: "#FFFFFF",
    black: "#000000",
  },
  primary: {
    // Violet — the "in flight / informational" tone (running, pending email
    // send, pending eDocs). Mirrors ora's primary.brand ramp.
    brand: {
      25: "#FCFAFF",
      50: "#F9F5FF",
      100: "#F4EBFF",
      200: "#E9D7FE",
      300: "#D6BBFB",
      400: "#BC92F6",
      500: "#9E77ED",
      600: "#7F56D9",
      700: "#6941C6",
      800: "#53389E",
      900: "#42307D",
      950: "#2C1C5F",
    },
    error: {
      25: "#FFFBFA",
      50: "#FEF3F2",
      100: "#FEE4E2",
      200: "#FECDCA",
      300: "#FDA29B",
      400: "#F97066",
      500: "#F04438",
      600: "#D92D20",
      700: "#B42318",
      800: "#912018",
      900: "#7A271A",
    },
    warning: {
      25: "#FFFCF5",
      50: "#FFFAEB",
      100: "#FEF0C7",
      200: "#FEDF89",
      300: "#FEC84B",
      400: "#FDB022",
      500: "#F79009",
      600: "#DC6803",
      700: "#B54708",
      800: "#93370D",
      900: "#7A2E0E",
    },
    success: {
      25: "#F6FEF9",
      50: "#ECFDF3",
      100: "#DCFAE6",
      200: "#ABEFC6",
      300: "#75E0A7",
      400: "#47CD89",
      500: "#17B26A",
      600: "#079455",
      700: "#067647",
      800: "#085D3A",
      900: "#074D31",
    },
    gray: {
      light: {
        25: "#FCFCFD",
        50: "#F9FAFB",
        100: "#F2F4F7",
        200: "#EAECF0",
        300: "#D0D5DD",
        400: "#98A2B3",
        500: "#667085",
        600: "#475467",
        700: "#344054",
        800: "#182230",
        900: "#101828",
        950: "#0C111D",
      },
    },
  },
} as const;

/**
 * Resolve a dash-delimited token name ("primary-gray-light-700", "sunrise")
 * to its hex value. Mirrors ora's `getColorHexCodeByColorName`: an unknown
 * name is returned unchanged so a typo degrades to an invalid CSS colour
 * rather than `undefined`.
 */
export const getColorHexCodeByColorName = (colorName: string): string => {
  let node: unknown = colors;
  for (const key of colorName.split("-")) {
    if (node === null || typeof node !== "object") return colorName;
    node = (node as Record<string, unknown>)[key];
    if (node === undefined) return colorName;
  }
  return typeof node === "string" ? node : colorName;
};
